/**
 * ShipmentLifecycleActions — P0 SPRINT (Feb 2026) · PERMANENT SHIPMENT ACCESS
 *
 * Kleine action-bar component met de archive/unarchive/soft-delete/restore
 * knoppen én de bevestig-dialogs. RBAC:
 *   - Archive/Unarchive:  quotes:edit     (Owner/Admin/Manager/Medewerker)
 *   - Soft delete/Restore: Owner + Admin only
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { Archive, ArchiveRestore, Trash2, RotateCcw, AlertTriangle } from "lucide-react";

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Textarea } from "../../ui/textarea";
import { Label } from "../../ui/label";

import { adminApi, useAuth, formatApiError } from "../../../context/AuthContext";
import { useGlobalLoading } from "../../../context/GlobalLoadingProvider";

export default function ShipmentLifecycleActions({ shipment, onChanged }) {
  const { user, hasPermission } = useAuth();
  const role = (user?.role || "").toLowerCase();
  const canArchive = hasPermission("quotes:edit");
  const canDelete = role === "owner" || role === "admin";
  const { runTask } = useGlobalLoading();

  const [archiveOpen, setArchiveOpen] = useState(false);
  const [unarchiveOpen, setUnarchiveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const isArchived = !!shipment?.archived_at;
  const isDeleted = !!shipment?.is_deleted;

  const call = async (endpoint, message, body = {}) => {
    setBusy(true);
    try {
      const res = await runTask({
        id: `lifecycle-${endpoint}-${shipment.id}`,
        message,
        run: () => adminApi.post(`/admin/shipping-labels/${shipment.id}/${endpoint}`, body),
      });
      toast.success(message.replace(/…$/, " voltooid"));
      onChanged?.(res.data);
      setReason("");
      return true;
    } catch (err) {
      toast.error(formatApiError(err) || "Actie mislukt");
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (!canArchive && !canDelete) return null;

  return (
    <>
      {canArchive && !isArchived && !isDeleted && (
        <Button size="sm" variant="outline"
                onClick={() => setArchiveOpen(true)}
                className="rounded-none text-xs"
                data-testid="shipment-detail-action-archive">
          <Archive className="w-3.5 h-3.5 mr-1.5" /> Archiveren
        </Button>
      )}
      {canArchive && isArchived && !isDeleted && (
        <Button size="sm" variant="outline"
                onClick={() => setUnarchiveOpen(true)}
                className="rounded-none text-xs bg-amber-50 border-amber-200 hover:bg-amber-100 text-amber-900"
                data-testid="shipment-detail-action-unarchive">
          <ArchiveRestore className="w-3.5 h-3.5 mr-1.5" /> Uit archief
        </Button>
      )}
      {canDelete && !isDeleted && (
        <Button size="sm" variant="outline"
                onClick={() => setDeleteOpen(true)}
                className="rounded-none text-xs bg-red-50 border-red-200 hover:bg-red-100 text-red-900"
                data-testid="shipment-detail-action-soft-delete">
          <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Verwijderen
        </Button>
      )}
      {canDelete && isDeleted && (
        <Button size="sm" variant="outline"
                onClick={() => setRestoreOpen(true)}
                className="rounded-none text-xs bg-emerald-50 border-emerald-200 hover:bg-emerald-100 text-emerald-900"
                data-testid="shipment-detail-action-restore">
          <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Herstellen
        </Button>
      )}

      {/* Archive dialog */}
      <Dialog open={archiveOpen} onOpenChange={(v) => !busy && setArchiveOpen(v)}>
        <DialogContent className="max-w-md" data-testid="shipment-archive-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-chivo font-bold">
              <Archive className="w-5 h-5 text-amber-700" /> Zending archiveren
            </DialogTitle>
            <DialogDescription>
              De zending {shipment?.tracking_number} wordt verplaatst naar het archief.
              Documenten, communicatie en historie blijven bewaard. Vindbaar via het
              filter &quot;Gearchiveerd&quot;.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">Reden (optioneel)</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
                      rows={2} data-testid="shipment-archive-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveOpen(false)} disabled={busy}>Annuleren</Button>
            <Button className="bg-amber-500 hover:bg-amber-600 text-white"
                    onClick={async () => {
                      const ok = await call("archive", "Zending wordt gearchiveerd…", { reason });
                      if (ok) setArchiveOpen(false);
                    }}
                    disabled={busy}
                    data-testid="shipment-archive-confirm">Archiveren</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unarchive dialog */}
      <Dialog open={unarchiveOpen} onOpenChange={(v) => !busy && setUnarchiveOpen(v)}>
        <DialogContent className="max-w-md" data-testid="shipment-unarchive-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-chivo font-bold">
              <ArchiveRestore className="w-5 h-5" /> Zending uit archief
            </DialogTitle>
            <DialogDescription>
              De zending komt weer terug in de actieve lijst.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">Reden (optioneel)</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
                      rows={2} data-testid="shipment-unarchive-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnarchiveOpen(false)} disabled={busy}>Annuleren</Button>
            <Button onClick={async () => {
                      const ok = await call("unarchive", "Zending wordt hersteld uit archief…", { reason });
                      if (ok) setUnarchiveOpen(false);
                    }}
                    disabled={busy}
                    data-testid="shipment-unarchive-confirm">Uit archief halen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Soft-delete dialog */}
      <Dialog open={deleteOpen} onOpenChange={(v) => !busy && setDeleteOpen(v)}>
        <DialogContent className="max-w-md" data-testid="shipment-soft-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-chivo font-bold text-red-800">
              <AlertTriangle className="w-5 h-5" /> Zending verwijderen (soft delete)
            </DialogTitle>
            <DialogDescription>
              De zending {shipment?.tracking_number} wordt gemarkeerd als verwijderd.
              Historie en documenten blijven bewaard. Alleen Owner/Admin kan zien of
              herstellen via filter &quot;Verwijderd&quot;.
              <br/>
              <strong>Reden is verplicht voor audit.</strong>
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">Reden *</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
                      rows={3} data-testid="shipment-soft-delete-reason"
                      placeholder="Verplicht — minimaal 3 tekens" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={busy}>Annuleren</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white"
                    onClick={async () => {
                      if ((reason || "").trim().length < 3) {
                        toast.error("Verplicht — reden minimaal 3 tekens.");
                        return;
                      }
                      const ok = await call("soft-delete", "Zending wordt verwijderd…", { reason });
                      if (ok) setDeleteOpen(false);
                    }}
                    disabled={busy}
                    data-testid="shipment-soft-delete-confirm">Verwijderen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore dialog */}
      <Dialog open={restoreOpen} onOpenChange={(v) => !busy && setRestoreOpen(v)}>
        <DialogContent className="max-w-md" data-testid="shipment-restore-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-chivo font-bold text-emerald-800">
              <RotateCcw className="w-5 h-5" /> Zending herstellen
            </DialogTitle>
            <DialogDescription>
              De zending wordt uit de verwijderd-status gehaald en verschijnt weer
              in de actieve lijst.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">Reden (optioneel)</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
                      rows={2} data-testid="shipment-restore-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreOpen(false)} disabled={busy}>Annuleren</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={async () => {
                      const ok = await call("restore", "Zending wordt hersteld…", { reason });
                      if (ok) setRestoreOpen(false);
                    }}
                    disabled={busy}
                    data-testid="shipment-restore-confirm">Herstellen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
